import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

import {
  ArrowRight,
  BookOpen,
  CheckCircle,
  ClipboardText,
  FileArrowDown,
  FileText,
  Handshake,
  Lightning,
  Lock,
  Question,
  Target,
  ChartLine,
  Warning,
  XCircle
} from '@phosphor-icons/react';
/**
 * Business Landing Page
 * 
 * Target Audience: Business leaders, operators, SMBs → mid-market → enterprise teams
 * Position: Business AI Operating System - self-service business analysis
 * Primary CTA: Start with an assessment
 * 
 * Key differentiator: Start with business analysis, not AI tools
 */

const BusinessLanding = () => {
  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  // Platform screenshots
  const screenshots = {
    assessments: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/tq41lo06_image.png",
    opportunities: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/c96qum0r_image.png",
    opportunityDetail: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/smf78g1r_image.png",
    agentBlueprint: "https://customer-assets.emergentagent.com/job_7fa1a542-7f3f-4261-aef6-52ab6c1a7cfb/artifacts/aqhkdnqt_image.png",
    createAgent: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/b5y1yonk_image.png",
    buildReport: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/kymh5n7d_image.png",
    executiveSummary: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/4863dznu_image.png",
    opportunityOverview: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/ex7n3ynt_image.png",
    workspace: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/83kji3lp_image.png",
    runAgent: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/7fa0k3sg_image.png",
    ebook: "https://customer-assets.emergentagent.com/job_bug-fix-maestro/artifacts/07gowykj_image.png",
  };

  // 4-step approach model
  const approachSteps = [
    {
      number: "1",
      title: "A new enquiry comes in",
      description: "",
      icon: ClipboardText,
      color: "#00E5C0"
    },
    {
      number: "2",
      title: "Your AI responds instantly",
      description: "",
      icon: Lightning,
      color: "#EC4899"
    },
    {
      number: "3",
      title: "Jobs get booked",
      description: "",
      icon: Target,
      color: "#10B981"
    },
    {
      number: "4",
      title: "Follow-ups happen automatically",
      description: "",
      icon: ChartLine,
      color: "#F59E0B"
    }
  ];

  return (
    <div className="bg-[#161B26] min-h-screen">
      <SEO 
        title="Deploy AI Automations Across Your Business | Man & Machine"
        description="Identify automation opportunities and run AI agents for sales, marketing, operations, and support. Free to install."
        canonicalUrl="https://manmachine.agency/businesses"
      />
      <LandingHeader />

      {/* ══════════════════════════════════════════════════════════════════════
          HERO SECTION - Simplified for conversion
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative pt-28 pb-24 overflow-hidden min-h-[80vh] flex items-center">
        {/* Background image - visible, subject on right */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_0553c8fb-e46a-4d53-a226-9746faedb587/artifacts/m8is5oly_Worker-img7.png"
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: 0.5, filter: 'saturate(0.6)', objectPosition: '70% center', transform: 'scale(0.7)', transformOrigin: 'right center' }}
          />
          {/* Asymmetric overlay: darker left (text readable), lighter right (image visible) */}
          <div className="absolute inset-0" style={{
            background: `
              linear-gradient(to right, #161B26 0%, rgba(22,27,38,0.85) 40%, rgba(22,27,38,0.4) 65%, rgba(22,27,38,0.2) 100%),
              linear-gradient(to bottom, #161B26 0%, transparent 15%, transparent 80%, #161B26 100%)
            `
          }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
          <div className="max-w-xl mr-auto ml-[5%]">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
              className="text-left"
            >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,229,192,0.1)] border border-[#00E5C0]/30 mb-5">
              <Lightning size={14} className="text-[#00E5C0]" />
              <span className="text-xs text-[#A0AEC0]">AI Systems for Growing Businesses</span>
            </div>

            <p className="text-base text-[#A0AEC0] mb-3 font-medium">For service business owners</p>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.12] mb-4">
              Never Miss a Lead.<br />
              <span className="text-[#00E5C0]">Never Chase Admin.</span>
            </h1>

            <p className="text-lg text-[#A0AEC0] mb-10 leading-relaxed max-w-2xl mx-auto">
              We handle your enquiries, bookings, and follow-ups automatically - so you can focus on the work.
            </p>

            {/* Primary CTA */}
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold text-lg px-10 py-4 rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.3)]"
            >
              Get My AI Plan
              <ArrowRight size={18} />
            </Link>

            {/* Supporting line */}
            <p className="text-sm text-[#7D8896] mt-4">
              See exactly what to automate in your business in under 60 seconds
            </p>
            
            {/* Bottom Feature Row */}
            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-10 mb-4">
              {[
                "Every lead answered instantly",
                "Jobs booked automatically",
                "Follow-ups sent on time",
                "Admin handled for you"
              ].map((point, idx) => (
                <span key={idx} className="flex items-center gap-2 text-sm text-[#8A919E]">
                  <CheckCircle size={14} className="text-[#10B981]/75" />
                  {point}
                </span>
              ))}
            </div>

            {/* Secondary CTA */}
            <div className="flex items-center gap-4 text-sm mt-2">
              <span className="text-[#5a5f72]">Already have an account?</span>
              <Link
                to="/login"
                className="text-[#00E5C0] hover:text-[#33EDCF] font-medium transition-colors flex items-center gap-1"
              >
                Sign in
                <ArrowRight size={12} />
              </Link>
            </div>
          </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PAIN POINTS SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">
              Most service businesses lose jobs in the <span className="text-[#00E5C0]">same places</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              {[
                "Missed enquiries",
                "Slow responses",
                "No follow-ups",
                "Admin backlog"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 glass rounded-xl">
                  <XCircle size={20} className="text-[#EC4899] flex-shrink-0" />
                  <p className="text-[#E2E8F0] font-medium">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          IDEAL FOR SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          {/* Ideal For */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass rounded-2xl p-8 border border-[rgba(16,185,129,0.2)]"
          >
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <CheckCircle size={22} className="text-[#10B981]" />
              Ideal For
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Trade businesses losing leads after hours",
                "Service teams spending hours on admin and follow-ups",
                "Growing businesses that need to book more jobs without more staff",
                "Owners who want to focus on the work, not the paperwork"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 text-[#E2E8F0]">
                  <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
                  {item}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          WHY MOST AI PROJECTS STALL (Replaces philosophical section)
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <div className="inline-flex p-3 rounded-xl bg-[rgba(236,72,153,0.1)] mb-6">
              <Warning size={28} className="text-[#EC4899]" />
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
              Why Most Businesses <span className="text-[#EC4899]">Keep Losing Leads</span>
            </h2>

            <div className="grid sm:grid-cols-3 gap-4 text-left mb-10">
              {[
                "Enquiries come in - no one replies fast enough",
                "Quotes go out - no one follows up",
                "Admin piles up - jobs get missed"
              ].map((point, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 glass rounded-xl">
                  <div className="w-6 h-6 rounded-full bg-[rgba(236,72,153,0.2)] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#EC4899] text-sm font-bold">{idx + 1}</span>
                  </div>
                  <p className="text-[#A0AEC0]">{point}</p>
                </div>
              ))}
            </div>

            <p className="text-lg text-white font-medium p-4 glass rounded-xl border-l-4 border-[#00E5C0] inline-block">
              Man & Machine fixes this by responding instantly, following up automatically, and handling admin in the background.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2 — THE APPROACH (4-Step Flow)
          ══════════════════════════════════════════════════════════════════════ */}
      <section id="approach" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Here's What Happens When{' '}
              <span className="text-[#00E5C0]">It's Running</span>
            </h2>
          </motion.div>

          {/* 4-Step Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approachSteps.map((step, idx) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                className="relative group"
              >
                <div className="glass rounded-2xl p-6 h-full border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.3)] transition-colors">
                  {/* Step number */}
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${step.color}20` }}
                  >
                    <span className="text-2xl font-bold" style={{ color: step.color }}>
                      {step.number}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          WHAT CHANGES SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
              What Changes in <span className="text-[#00E5C0]">Your Business</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              {[
                { text: "Every enquiry gets answered", icon: CheckCircle },
                { text: "Jobs get booked faster", icon: CheckCircle },
                { text: "Follow-ups happen without chasing", icon: CheckCircle },
                { text: "Admin disappears", icon: CheckCircle }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 glass rounded-xl">
                  <item.icon size={20} className="text-[#10B981] flex-shrink-0" />
                  <p className="text-[#E2E8F0] font-medium">{item.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          WHAT WE CAN AUTOMATE SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
              What We Can <span className="text-[#00E5C0]">Automate for You</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Target, text: "Never miss a lead", color: "#00E5C0" },
                { icon: ClipboardText, text: "Booking assistant", color: "#10B981" },
                { icon: Lightning, text: "Follow-up system", color: "#EC4899" },
                { icon: FileText, text: "Invoice reminders", color: "#F59E0B" }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: idx * 0.08 }}
                  className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.3)] transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${item.color}20` }}>
                    <item.icon size={24} style={{ color: item.color }} />
                  </div>
                  <p className="text-[#E2E8F0] font-medium">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 5 — WORKS WITH YOUR TOOLS
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <div className="inline-flex p-3 rounded-xl bg-[rgba(0,229,192,0.1)] mb-6">
              <Handshake size={28} className="text-[#00E5C0]" />
            </div>
              
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Works with the Tools <span className="text-[#00E5C0]">You Already Use</span>
            </h2>

            <p className="text-lg text-[#A0AEC0] mb-6 leading-relaxed max-w-xl mx-auto">
              No disruption. No new systems to learn.
            </p>
              
            <p className="text-base text-[#A0AEC0] leading-relaxed max-w-xl mx-auto">
              If you need hands-on help with setup, our certified partners can step in at any point.
            </p>

            <Link
              to="/partners"
              className="inline-flex items-center gap-2 mt-6 text-[#00E5C0] font-medium hover:text-[#33EDCF] transition-colors"
            >
              Find a certified partner
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 6 — RESOURCES (eBook)
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Not Ready Yet?
            </h2>
            <p className="text-lg text-[#A0AEC0] max-w-2xl mx-auto">
              Learn how service businesses are saving time and winning more jobs.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass rounded-2xl p-8 md:p-12 border border-[rgba(0,229,192,0.2)]"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* eBook Cover */}
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src={screenshots.ebook}
                  alt="Man & Machine: Where Intelligence Meets Imagination - The AI Transformation Playbook for Business"
                  className="w-full h-auto"
                />
              </div>

              {/* eBook Info */}
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[rgba(0,229,192,0.1)] border border-[rgba(0,229,192,0.3)] mb-4">
                  <BookOpen size={14} className="text-[#00E5C0]" />
                  <span className="text-sm text-[#00E5C0] font-medium">Free eBook</span>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4">
                  Man & Machine: Where Intelligence Meets Imagination
                </h3>
                
                <p className="text-[#A0AEC0] mb-6">
                  The AI Transformation Playbook for Business. Learn how to implement AI with structure, governance, and measurable outcomes.
                </p>

                <Link
                  to="/resources"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold rounded-xl transition-colors"
                >
                  <FileArrowDown size={18} />
                  Download free guide
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PRICING ANCHOR SECTION (No prices shown)
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Plans Designed for <span className="text-[#00E5C0]">Growing Businesses</span>
            </h2>
            <p className="text-lg text-[#A0AEC0] max-w-2xl mx-auto mb-6">
              Start capturing more leads and saving time from day one. Scale as your business grows.
            </p>

            <div className="glass rounded-2xl p-8 border border-[rgba(0,229,192,0.3)]">
              <div className="grid sm:grid-cols-2 gap-6 text-left mb-8">
                {[
                  "Every lead captured instantly",
                  "Automatic follow-ups on quotes",
                  "Jobs booked without back-and-forth",
                  "Clear reporting on what's working",
                  "Team access controls",
                  "Secure, controlled automation"
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-[#10B981] flex-shrink-0" />
                    <span className="text-[#E2E8F0]">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap justify-center gap-4 mb-4">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold rounded-xl transition-colors"
                >
                  Start Free – No Credit Card
                  <ArrowRight size={18} />
                </Link>
              </div>
              
              <p className="text-sm text-[#718096]">
                Contact sales for enterprise environments.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FAQ SECTION - Objection Handling
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Frequently Asked Helps
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "Do I need developers?",
                a: "No. If you can use email and a calendar, you can use Man & Machine. No coding, no technical setup."
              },
              {
                q: "How long does it take to get started?",
                a: "Most businesses are up and running within a day. You'll see where you're losing leads and time in the first few minutes."
              },
              {
                q: "Can this work with my current systems?",
                a: "Yes. Man & Machine connects to the tools you already use. No need to change how you work."
              },
              {
                q: "Is this just for big companies?",
                a: "No. It's built for service businesses of any size. From solo operators to teams of 50+."
              },
              {
                q: "Is my data secure?",
                a: "Yes. Your data stays yours. Full audit trails and admin controls are built in."
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.05)]"
              >
                <div className="flex items-start gap-3">
                  <Question size={20} className="text-[#00E5C0] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                    <p className="text-[#A0AEC0] text-sm">{faq.a}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FINAL CTA SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Stop Losing Leads.{' '}
              <span className="text-[#00E5C0]">Start Winning More Jobs.</span>
            </h2>
            
            <p className="text-lg text-[#A0AEC0] mb-10">
              See exactly what to automate in your business.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] text-lg font-semibold rounded-xl transition-colors"
              >
                Get My AI Plan
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/automations"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-lg font-semibold rounded-xl hover:bg-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)] transition-all"
              >
                See It In Action
              </Link>
            </div>
            
            <p className="text-sm text-[#718096]">
              No credit card required. See results in days, not months.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessLanding;
